import { WarningFilled } from '@ant-design/icons'
import { areAllRepurchaseWarrantyResultsAcceptedOrCommented } from '@app/store/reducers/submissionReviews/submissionReviewsSelectors'
import { Button as AntdButton, Modal, Tag } from 'antd'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import ApproveSubmissionModal from './ReggoraReview/ApproveSubmissionModal'
import AddComment from './ReggoraReview/ResultTable/AddComment'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { selectOrderSupportsRepurchaseWarranty } from '@app/store/reducers/order/orderSelectors'
import { FINALIZED_WARRANTY_STATUSES } from '@app/common/constants/warranty'

const WARRANTY_FINDINGS_REQUIRED_MESSAGE = 'There are warranty findings that need to be resolved before approving.'

export const ApprovalWarrantyBanner = ({ isVisible }) => {
  if (!isVisible) {
    return null
  }

  return (
    <Tag
      className="h5 status-bar flex-grow-1 d-flex align-items-center justify-content-center"
      color="warning"
      data-testid="warranty-banner-approval"
      style={{ padding: '0.5rem 1rem', margin: '0', width: '100%', fontWeight: 'bold', marginBottom: '14px' }}
    >
      <WarningFilled />
      <span className="ml-1">{WARRANTY_FINDINGS_REQUIRED_MESSAGE}</span>
    </Tag>
  )
}

const ApproveSubmissionButton = ({
  addVendorScore,
  approveReport,
  currentUserId,
  hasReggoraResult,
  is_amc_lender,
  orderData,
  refreshOrder,
  sub,
  submissionComment,
  setSubmissionComment,
}) => {
  const [isApproveSubmissionModalOpen, setIsApproveSubmissionModalOpen] = useState(false)
  const flags = useFlags() || {}

  const areAllAcceptedOrCommented = useSelector((state) =>
    areAllRepurchaseWarrantyResultsAcceptedOrCommented(state, sub?.version),
  )
  const orderSupportsRepurchaseWarranty = useSelector(selectOrderSupportsRepurchaseWarranty)
  const hasSubmissionWarranty = FINALIZED_WARRANTY_STATUSES.includes(sub?.repurchase_warranty?.status)
  const isWarrantyBannerVisible = Boolean(
    flags?.ampManualExpandedWarranty &&
    is_amc_lender &&
    orderSupportsRepurchaseWarranty &&
    hasSubmissionWarranty &&
    !areAllAcceptedOrCommented,
  )
  const approveButtonDisabled = isWarrantyBannerVisible
    ? {
      disabled: true,
      style: { background: 'initial', border: '1px solid' },
      title: WARRANTY_FINDINGS_REQUIRED_MESSAGE,
    }
    : {}

  const showApproveSubmissionModal = () => {
    setIsApproveSubmissionModalOpen(true)
  }

  const handleApproveSubmissionModalClose = () => {
    setIsApproveSubmissionModalOpen(false)
  }

  const handleCommentInputChange = (e) => {
    setSubmissionComment(e.target.value)
  }

  const handleApproveSubmission = async () => {
    await approveReport.put({
      id: orderData.id,
      version: sub.version,
      user_id: currentUserId,
      approval_comment: submissionComment,
    })

    handleApproveSubmissionModalClose()
    refreshOrder()
    if (is_amc_lender && orderData.job_type === 'amc') {
      addVendorScore({ orderId: orderData.id, panelistId: orderData.accepted?.id })
    }
  }

  return (
    <>
      <AntdButton
        data-testid="approve-submission-button"
        onClick={showApproveSubmissionModal}
        style={{ background: '#005DB4', borderColor: 'white' }}
        type="primary"
      >
        Approve Submission
      </AntdButton>
      <Modal
        data-testid="approve-submission-modal"
        className={hasReggoraResult ? 'comment-modal with-comment' : 'comment-modal'}
        visible={isApproveSubmissionModalOpen}
        onCancel={handleApproveSubmissionModalClose}
        closable={false}
        footer={[
          <AntdButton key="back" onClick={handleApproveSubmissionModalClose}>
            Cancel
          </AntdButton>,
          <AntdButton
            key="submit"
            type="primary"
            loading={approveReport.loading}
            onClick={handleApproveSubmission}
            {...approveButtonDisabled}
          >
            Approve
          </AntdButton>,
        ]}
      >
        <ApproveSubmissionModal
          title={'Approve Report'}
          description={'Are you sure you want to approve this report?'}
        />
        <ApprovalWarrantyBanner isVisible={isWarrantyBannerVisible} />
        {hasReggoraResult && (
          <AddComment comment={submissionComment} onChange={handleCommentInputChange} minRows={25} maxRows={100} />
        )}
      </Modal>
    </>
  )
}

export default ApproveSubmissionButton
